/**
 * Taalcontext, gedeeld door alle panes.
 *
 * De pane laadt de taal (werkmap-eigenschap of Excel-taal), zet die hier en biedt een
 * wisselknop in de titelbalk. Onderliggende componenten lezen de taal met `useLang()`;
 * elke pane houdt zijn eigen vertaaltabel en bouwt daar zijn `useT()` op (zie
 * `src/apps/docsense/utils/LangContext.tsx`). Zonder provider valt het terug op de taal
 * van Excel, zodat losse consumenten (bv. de Sense-pane die dezelfde viewer gebruikt)
 * ook een zinnige taal hebben.
 */

import * as React from "react";
import { Lang, detectExcelLanguage, loadLanguage, saveLanguage, otherLanguage } from "./language";

const LangContext = React.createContext<Lang | null>(null);

export function LangProvider({
  lang,
  children,
}: {
  lang: Lang;
  children: React.ReactNode;
}): JSX.Element {
  return <LangContext.Provider value={lang}>{children}</LangContext.Provider>;
}

/** Huidige taal (met terugval op de Excel-taal als er geen provider is). */
export function useLang(): Lang {
  const fromContext = React.useContext(LangContext);
  const fallback = React.useMemo(() => detectExcelLanguage(), []);
  return fromContext ?? fallback;
}

/**
 * Taalstatus voor een pane: laadt de bewaarde taal bij het openen en bewaart een wissel.
 * Geeft `[lang, toggle]` terug, klaar voor `PaneHeader language={{ lang, onToggle }}`.
 */
export function useLanguageSetting(initial: Lang = "NL"): [Lang, () => void, (lang: Lang) => void] {
  const [lang, setLang] = React.useState<Lang>(initial);
  React.useEffect(() => {
    let alive = true;
    loadLanguage()
      .then((l) => {
        if (alive) setLang(l);
      })
      .catch(() => {
        /* houd de beginwaarde */
      });
    return () => {
      alive = false;
    };
  }, []);
  const set = React.useCallback((next: Lang) => {
    setLang(next);
    void saveLanguage(next);
  }, []);
  const toggle = React.useCallback(() => {
    setLang((prev) => {
      const next = otherLanguage(prev);
      void saveLanguage(next);
      return next;
    });
  }, []);
  return [lang, toggle, set];
}
